import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, MoreHorizontal, Flag, EyeOff, Eye, Trash2, Globe2, Lock, Send, HandHeart, BadgeCheck } from "lucide-react";

export const REACTIONS = [
  { key: "like", emoji: "👍", label: "Like" },
  { key: "love", emoji: "❤️", label: "Love" },
  { key: "pray", emoji: "🙏", label: "Praying" },
  { key: "amen", emoji: "🙌", label: "Amen" },
  { key: "haha", emoji: "😂", label: "Haha" },
  { key: "wow", emoji: "😮", label: "Wow" },
  { key: "sad", emoji: "😢", label: "Sad" },
];
const R = Object.fromEntries(REACTIONS.map((r) => [r.key, r]));

const ago = (iso) => {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export const fileUrl = (src) => (src ? `${API.replace(/\/api$/, "")}${src}` : null);

export function Avatar({ name, photo, size = 40, userId, testid }) {
  const inner = photo ? (
    <img src={fileUrl(photo)} alt={name} className="w-full h-full object-cover" />
  ) : (
    <span className="font-serif-display" style={{ fontSize: size * 0.42 }}>{(name || "?")[0].toUpperCase()}</span>
  );
  const cls = "rounded-full overflow-hidden flex items-center justify-center shrink-0 transition-transform hover:scale-105";
  const style = { width: size, height: size, background: "var(--surface-hover)", color: "var(--gold)" };
  return userId ? <Link to={`/social/u/${userId}`} className={cls} style={style} data-testid={testid}>{inner}</Link> : <span className={cls} style={style} data-testid={testid}>{inner}</span>;
}

export function SupporterBadge({ show, testid }) {
  if (!show) return null;
  return (
    <span className="inline-flex items-center gap-0.5 text-[0.55rem] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full align-middle" style={{ background: "rgba(230,184,92,0.15)", color: "var(--gold)" }} title="Supports thedisciple with a $4.99/mo membership" data-testid={testid}>
      <BadgeCheck size={10} /> Supporter
    </span>
  );
}

function PrayBar({ post, onChange }) {
  const [names, setNames] = useState(null);
  const pray = async () => {
    try { const r = await api.post(`/social/posts/${post.id}/pray`); onChange(r.data); setNames(null); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't update."); }
  };
  const toggleNames = async () => {
    if (names) { setNames(null); return; }
    const r = await api.get(`/social/posts/${post.id}/prayers`); setNames(r.data.prayers);
  };
  return (
    <div className="mt-4" data-testid={`pray-bar-${post.id}`}>
      <div className="flex items-center gap-3">
        <button onClick={pray} className="flex items-center gap-2 text-sm px-4 py-2 rounded-full transition-colors" style={{ background: post.my_prayed ? "#EC4899" : "rgba(236,72,153,0.12)", color: post.my_prayed ? "#FFF" : "#F9A8D4" }} data-testid={`pray-btn-${post.id}`}>
          <HandHeart size={15} /> {post.my_prayed ? "You prayed" : "I prayed"}
        </button>
        {post.prayed_count > 0 && (
          <button onClick={toggleNames} className="text-xs hover:underline" style={{ color: "var(--ink-2)" }} data-testid={`pray-count-${post.id}`}>
            {post.prayed_count} {post.prayed_count === 1 ? "person has" : "people have"} prayed
          </button>
        )}
      </div>
      {names && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`pray-names-${post.id}`}>
          {names.map((n, i) => <span key={i} className="text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--surface-hover)", color: "var(--ink-2)" }}>{n.user_name}</span>)}
        </div>
      )}
    </div>
  );
}

function ReactionBar({ post, onChange }) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const react = async (type) => {
    setOpen(false);
    try { const r = await api.post(`/social/posts/${post.id}/react`, { type }); onChange(r.data); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't react."); }
  };
  const mine = post.my_reaction ? R[post.my_reaction] : null;
  return (
    <div className="relative" onMouseEnter={() => { clearTimeout(timer.current); setOpen(true); }} onMouseLeave={() => { timer.current = setTimeout(() => setOpen(false), 300); }}>
      {open && (
        <div className="absolute bottom-full left-0 mb-2 flex gap-1 px-2 py-1.5 rounded-full shadow-xl z-10" style={{ background: "var(--bg-deep)", border: "1px solid var(--border-strong)" }} data-testid={`reaction-palette-${post.id}`}>
          {REACTIONS.map((r) => (
            <button key={r.key} onClick={() => react(r.key)} title={r.label} className="text-xl leading-none px-1 transition-transform duration-150 hover:scale-125 hover:-translate-y-1" data-testid={`react-${r.key}-${post.id}`}>{r.emoji}</button>
          ))}
        </div>
      )}
      <button onClick={() => react(mine ? null : "like")} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full hover:bg-[var(--surface-hover)] transition-colors" style={{ color: mine ? "var(--gold)" : "var(--ink-2)" }} data-testid={`react-btn-${post.id}`}>
        <span>{mine ? mine.emoji : "👍"}</span> {mine ? mine.label : "React"}
      </button>
    </div>
  );
}

function ReactionSummary({ counts }) {
  const entries = Object.entries(counts || {}).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((s, [, n]) => s + n, 0);
  if (!total) return null;
  return (
    <span className="flex items-center gap-1 text-xs" style={{ color: "var(--muted)" }}>
      <span>{entries.slice(0, 3).map(([k]) => R[k]?.emoji).join("")}</span> {total}
    </span>
  );
}

function Comments({ post, canContribute, onCount }) {
  const [rows, setRows] = useState(null);
  const [text, setText] = useState("");
  useEffect(() => { api.get(`/social/posts/${post.id}/comments`).then((r) => setRows(r.data.comments)); }, [post.id]);
  const add = async () => {
    if (!text.trim()) return;
    try { const r = await api.post(`/social/posts/${post.id}/comments`, { body: text }); setRows((x) => [...x, r.data]); setText(""); onCount(1); }
    catch (err) { toast.error(err?.response?.data?.detail?.message || "Couldn't comment."); }
  };
  const del = async (c) => {
    await api.delete(`/social/comments/${c.id}`); setRows((x) => x.filter((y) => y.id !== c.id)); onCount(-1);
  };
  return (
    <div className="pt-3 space-y-2" data-testid={`comments-${post.id}`}>
      {(rows || []).map((c) => (
        <div key={c.id} className="flex items-start gap-2 group">
          <Avatar name={c.author_name} photo={c.author_photo} size={28} userId={c.author_id} testid={`comment-avatar-${c.id}`} />
          <div className="flex-1 px-3 py-2 rounded-2xl text-sm" style={{ background: "var(--surface-hover)" }}>
            <Link to={`/social/u/${c.author_id}`} className="font-medium mr-1 hover:underline" style={{ color: "var(--ink)" }} data-testid={`comment-author-${c.id}`}>{c.author_name}</Link>
            <SupporterBadge show={c.author_supporter} testid={`comment-supporter-${c.id}`} />
            <span className="mr-1" />
            <span style={{ color: "var(--ink-2)" }}>{c.body}</span>
          </div>
          {c.can_delete && <button onClick={() => del(c)} className="opacity-0 group-hover:opacity-100 p-1" style={{ color: "var(--muted)" }} data-testid={`comment-delete-${c.id}`}><Trash2 size={12} /></button>}
        </div>
      ))}
      {canContribute ? (
        <div className="flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Write a comment…" className="flex-1 px-3 py-2 text-sm rounded-full" data-testid={`comment-input-${post.id}`} />
          <button onClick={add} className="p-2 rounded-full" style={{ color: "var(--gold)" }} data-testid={`comment-send-${post.id}`}><Send size={16} /></button>
        </div>
      ) : (
        <p className="text-xs" style={{ color: "var(--muted)" }}><a href="/membership" className="underline" style={{ color: "var(--gold)" }}>Become a member</a> ($4.99/mo) to join the conversation.</p>
      )}
    </div>
  );
}

function PostMenu({ post, onChange, onRemove }) {
  const [open, setOpen] = useState(false);
  const act = async (fn, msg) => { setOpen(false); try { await fn(); toast.success(msg); } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't do that."); } };
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="p-1.5 rounded-full hover:bg-[var(--surface-hover)]" style={{ color: "var(--muted)" }} data-testid={`post-menu-${post.id}`}><MoreHorizontal size={16} /></button>
      {open && (
        <div className="absolute right-0 mt-1 w-44 rounded-md py-1 z-20 shadow-xl" style={{ background: "var(--bg-deep)", border: "1px solid var(--border-strong)" }}>
          <button onClick={() => act(() => api.post(`/social/posts/${post.id}/report`, { reason: "" }), "Reported to church leaders.")} className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-[var(--surface-hover)]" style={{ color: "var(--ink-2)" }} data-testid={`post-report-${post.id}`}><Flag size={13} /> Report</button>
          {post.can_moderate && (
            <button onClick={() => act(async () => { const r = await api.post(`/social/posts/${post.id}/hide`, { hidden: !post.hidden }); onChange(r.data); }, post.hidden ? "Post visible again." : "Post hidden.")} className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-[var(--surface-hover)]" style={{ color: "var(--ink-2)" }} data-testid={`post-hide-${post.id}`}>{post.hidden ? <Eye size={13} /> : <EyeOff size={13} />} {post.hidden ? "Unhide" : "Hide"}</button>
          )}
          {post.can_delete && (
            <button onClick={() => window.confirm("Delete this post?") && act(async () => { await api.delete(`/social/posts/${post.id}`); onRemove(post.id); }, "Post deleted.")} className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-[var(--surface-hover)]" style={{ color: "#F87171" }} data-testid={`post-delete-${post.id}`}><Trash2 size={13} /> Delete</button>
          )}
        </div>
      )}
    </div>
  );
}

export default function PostCard({ post, canContribute, onChange, onRemove }) {
  const [showComments, setShowComments] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const long = post.kind === "article" && post.body.length > 600;
  const body = long && !expanded ? `${post.body.slice(0, 600)}…` : post.body;
  return (
    <article className="rounded-xl p-5 social-in" style={{ background: "var(--bg-alt)", border: `1px solid ${post.hidden ? "#F8717160" : post.kind === "prayer" ? "rgba(236,72,153,0.35)" : "var(--border-subtle)"}` }} data-testid={`post-${post.id}`}>
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar name={post.author_name} photo={post.author_photo} userId={post.author_id} testid={`post-avatar-${post.id}`} />
          <div className="min-w-0">
            <div className="text-sm font-medium truncate flex items-center gap-1.5" style={{ color: "var(--ink)" }}><Link to={`/social/u/${post.author_id}`} className="hover:underline truncate" data-testid={`post-author-${post.id}`}>{post.author_name}</Link> <SupporterBadge show={post.author_supporter} testid={`post-supporter-${post.id}`} /></div>
            <div className="text-[0.7rem] flex items-center gap-1.5" style={{ color: "var(--muted)" }}>
              {post.org_name} · {ago(post.created_at)} · {post.visibility === "public" ? <Globe2 size={10} /> : <Lock size={10} />}
              {post.hidden && <span style={{ color: "#F87171" }}>· hidden</span>}
              {post.report_count > 0 && <span style={{ color: "#F87171" }} data-testid={`post-reports-${post.id}`}>· {post.report_count} report{post.report_count > 1 ? "s" : ""}</span>}
            </div>
          </div>
        </div>
        <PostMenu post={post} onChange={onChange} onRemove={onRemove} />
      </header>
      {post.kind === "prayer" && (
        <div className="mt-4 inline-flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full" style={{ background: "rgba(236,72,153,0.12)", color: "#F9A8D4" }} data-testid={`post-prayer-label-${post.id}`}><HandHeart size={11} /> Prayer request</div>
      )}
      {post.kind === "article" && <h3 className="font-serif-display text-2xl mt-4 leading-tight" style={{ color: "var(--ink)" }}>{post.title}</h3>}
      {body && <p className="mt-3 text-[0.95rem] leading-relaxed whitespace-pre-line" style={{ color: "var(--ink-2)" }}>{body}</p>}
      {long && <button onClick={() => setExpanded(!expanded)} className="text-xs mt-1" style={{ color: "var(--gold)" }} data-testid={`post-readmore-${post.id}`}>{expanded ? "Show less" : "Read more"}</button>}
      {post.images?.length > 0 && (
        <div className={`mt-3 grid gap-1.5 ${post.images.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
          {post.images.map((src) => <img key={src} src={fileUrl(src)} alt="" className="w-full max-h-[28rem] object-cover rounded-lg" loading="lazy" />)}
        </div>
      )}
      {post.kind === "prayer" && <PrayBar post={post} onChange={onChange} />}
      <div className="mt-4 flex items-center justify-between text-xs" style={{ color: "var(--muted)" }}>
        <ReactionSummary counts={post.reaction_counts} />
        {post.comment_count > 0 && <span>{post.comment_count} comment{post.comment_count > 1 ? "s" : ""}</span>}
      </div>
      <div className="mt-2 pt-2 flex items-center gap-1" style={{ borderTop: "1px solid var(--border-subtle)" }}>
        <ReactionBar post={post} onChange={onChange} />
        <button onClick={() => setShowComments(!showComments)} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full hover:bg-[var(--surface-hover)] transition-colors" style={{ color: "var(--ink-2)" }} data-testid={`comment-toggle-${post.id}`}><MessageCircle size={15} /> Comment</button>
      </div>
      {showComments && <Comments post={post} canContribute={canContribute} onCount={(d) => onChange({ ...post, comment_count: post.comment_count + d })} />}
    </article>
  );
}
